// HR zones — Karvonen method (D-069, Session Data Model v1.8 §8.1).
// Zone % = (HR − rest) / (max − rest) × 100, floored at 0.
// <55 UT3 | 55–69 UT2 | 70–79 UT1 | 80–84 AT | 85–94 TR | ≥95 AN

export const HR_ZONE_METHOD = 'karvonen';
export const DEFAULT_REST_HR = 60;
export const DEFAULT_MAX_HR = 185;
export const REST_HR_RANGE = [30, 100];
export const MAX_HR_RANGE = [120, 230];

export function ageFromDobIso(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a -= 1;
  return a >= 0 ? a : null;
}

export const estimatedMaxHr = (age) => (age ? Math.round(208 - 0.7 * age) : null);

// Rower's own max → 208 − 0.7 × age → 185.
export function maxHrInUse(user) {
  const own = user?.hr_max_setting_bpm ?? user?.max_hr;
  if (own) return own;
  return estimatedMaxHr(ageFromDobIso(user?.dob)) || DEFAULT_MAX_HR;
}

export const restHrInUse = (user) => user?.hr_rest_bpm ?? user?.resting_hr ?? DEFAULT_REST_HR;

export function hrrPercent(hr, maxHr, restHr = 0) {
  if (!hr || !maxHr || maxHr <= restHr) return null;
  return Math.max(0, Math.round(((hr - restHr) / (maxHr - restHr)) * 100));
}

export const ZONE_KEYS = ['ut3', 'ut2', 'ut1', 'at', 'tr', 'an'];

export function classifyZone(hr, maxHr, restHr = 0) {
  const pct = hrrPercent(hr, maxHr, restHr);
  if (pct == null) return null;
  if (pct < 55) return 'ut3';
  if (pct < 70) return 'ut2';
  if (pct < 80) return 'ut1';
  if (pct < 85) return 'at';
  if (pct < 95) return 'tr';
  return 'an';
}

const ZONE_INFO = {
  an: { zone: 'Anaerobic', color: '#EF4444', bgColor: '#FEE2E2', label: 'Anaerobic' },
  tr: { zone: 'Transport', color: '#F97316', bgColor: '#FFEDD5', label: 'Oxygen Transport' },
  at: { zone: 'Threshold', color: '#EAB308', bgColor: '#FEF9C3', label: 'Anaerobic Threshold' },
  ut1: { zone: 'Utilization 1', color: '#22C55E', bgColor: '#DCFCE7', label: 'Oxygen Utilisation 1' },
  ut2: { zone: 'Utilization 2', color: '#3B82F6', bgColor: '#DBEAFE', label: 'Oxygen Utilisation 2' },
  ut3: { zone: 'Utilization 3', color: '#71717A', bgColor: '#F4F4F5', label: 'Recovery' },
};

export function calculateHRZone(currentHR, maxHR, restHR = 0) {
  const key = classifyZone(currentHR, maxHR, restHR);
  return key ? { key, pct: hrrPercent(currentHR, maxHR, restHR), ...ZONE_INFO[key] } : null;
}

// Hard rules for the profile: ranges + resting below the max in use.
export function validateHrSettings({ rest, max, dob }) {
  if (rest != null && (rest < REST_HR_RANGE[0] || rest > REST_HR_RANGE[1])) return `Resting HR must be ${REST_HR_RANGE[0]}–${REST_HR_RANGE[1]} bpm`;
  if (max != null && (max < MAX_HR_RANGE[0] || max > MAX_HR_RANGE[1])) return `Max HR must be ${MAX_HR_RANGE[0]}–${MAX_HR_RANGE[1]} bpm`;
  const maxUsed = max || estimatedMaxHr(ageFromDobIso(dob)) || DEFAULT_MAX_HR;
  if ((rest ?? DEFAULT_REST_HR) >= maxUsed) return `Resting HR must be lower than your max HR (${maxUsed} bpm)`;
  return null;
}

/**
 * @param {Array<{t:number,v:number}>} hrSeries — 1 Hz HR samples
 * @returns {Object|null} { ut3, ut2, ut1, at, tr, an } fractions (time in zone ÷ total)
 */
export function computeHrZoneDistribution(hrSeries, maxHr, restHr = 0) {
  if (!Array.isArray(hrSeries) || hrSeries.length === 0) return null;
  if (!maxHr || maxHr <= 0) return null;
  const counts = { ut3: 0, ut2: 0, ut1: 0, at: 0, tr: 0, an: 0 };
  let total = 0;
  for (const s of hrSeries) {
    const z = classifyZone(s?.v, maxHr, restHr);
    if (z) { counts[z] += 1; total += 1; }
  }
  if (total === 0) return null;
  const out = {};
  for (const k of ZONE_KEYS) out[k] = counts[k] / total;
  return out;
}

export function buildHrZoneSeries(hrSeries, maxHr, restHr = 0) {
  if (!Array.isArray(hrSeries) || !maxHr || maxHr <= 0) return [];
  const out = [];
  for (const s of hrSeries) {
    const z = classifyZone(s?.v, maxHr, restHr);
    if (z) out.push({ t: s.t, v: z });
  }
  return out;
}

export function averageSeries(series) {
  if (!Array.isArray(series) || series.length === 0) return null;
  let sum = 0;
  let n = 0;
  for (const s of series) {
    if (typeof s?.v === 'number' && Number.isFinite(s.v)) { sum += s.v; n += 1; }
  }
  return n > 0 ? sum / n : null;
}
